import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import { SvgBack as BackIcon } from "melodies-source/Svgs/Back";
import { Stats } from "./Stats";
import { ChartCard, Props as ChartCardProps } from "../../Components/ChartCard";
import { H2, H3 } from "melodies-source/Text";
import { Divider } from "Components/Divider";
import { NoData } from "Components/NoData";
import * as Sentry from "@sentry/react";
import { toast } from "react-hot-toast";
import { Button } from "melodies-source/Button";
import { ReactComponent as DownloadIcon } from "assets/svg/download.svg";
import { useMobileMediaQuery } from "hooks/useMobileMediaQuery";
import { useDocumentData } from "react-firebase-hooks/firestore";
import { doc, getFirestore } from "firebase/firestore";
import {
  Locations,
  Question,
  Questions,
  RankedQuestion,
  TextQuestion,
} from "models/survey";
import { Spinner } from "melodies-source/Spinner";
import { Filter } from "./Filter";
import { useTabletMediaQuery } from "hooks/useTabletMediaQuery";
import { getDownloadURL, getStorage, ref } from "firebase/storage";
import { downloadFromBlob } from "Utils/file";
import { useArtistContext } from "Components";
import { useArtistSurveys } from "hooks/useArtistSurveys";
import { DateTime } from "luxon";
import { FreeResponsesModal } from "../../Components/FreeResponsesModal";
import { SweepsCard } from "../Components/Sweeps/SweepsCard";
import { Ref } from "refs";
import { findPreSaveSurveyConfigModule } from "@max/common";
import { PreSaves } from "Routes/Reporting/SetBio/PreSaves";

const FanLocations = React.lazy(
  () => import("Routes/Reporting/Components/FanLocations"),
);

interface Context {
  questions: Question[];
}

const SurveyQuestionsContext = createContext<Nullable<Context>>(null);

export const SurveyDetails: React.FC = () => {
  const { name: artistName, logAction } = useArtistContext();
  const [selectedQuestion, setSelectedQuestion] = useState("all");
  const [selectedOption, setSelectedOption] = useState("all");
  const [resultsLoading, setResultsLoading] = useState(false);
  const { artistId, surveyId } = useParams<{
    artistId: string;
    surveyId: string;
  }>();
  const isMobile = useMobileMediaQuery();
  const isTablet = useTabletMediaQuery();
  const [aggregations, aggregationsLoading, aggregationsError] =
    useDocumentData<Questions>(
      doc(
        getFirestore(),
        `sts3_surveys/${surveyId}/aggregations/${
          selectedOption !== "all"
            ? `${selectedQuestion}#${selectedOption}`
            : "prod"
        }`,
      ),
    );

  // if clicking survey details at bottom of list, scroll back to top of details page
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  // Due to how we handle permissions for additional groups on a campaign,
  // we read most of the survey data from typesense. this allows us to use the
  // rules we set up on the backend in order to limit access to only those with perms.
  // however, we do need to check if a sweeps property exists on the survey root doc,
  // so we pull that document from firestore and check if the property exists. this
  // read will fail for permissions errors on `additionalGroups`, which is currently
  // what we want.
  const { documents: surveys, loading: surveyDocLoading } = useArtistSurveys({
    queryBy: "title",
    resultsPerPage: 1,
    filterBy: [`surveyId:${surveyId}`].join(" && "),
  });

  // NOTE: this will fail for all `additionalGroups`! don't use this document data
  // for anyone other than the creating artist group for the campaign. if you need
  // other properties, use the typesense document above.
  const [surveyRootDoc] = useDocumentData(
    Ref("sts3_surveys/{surveyId}", { surveyId }),
  );

  const [surveyProdVersion] = useDocumentData(
    Ref("sts3_surveys/{surveyId}/versions/{versionId}", {
      surveyId,
      versionId: "prod",
    }),
  );

  const preSaveModule = useMemo(
    () => findPreSaveSurveyConfigModule(surveyProdVersion?.pages),
    [surveyProdVersion?.pages],
  );

  const [allRespondentsQuestions, setAllRespondentsQuestions] =
    useState<Question[]>(null);

  const [surveyQuestions, setSurveyQuestions] = useState<Question[]>(null);

  const locations: Locations = aggregations?.locations as unknown as Locations;

  const surveyTypesense = surveys?.length ? surveys[0] : undefined;

  const downloadSummary = async () => {
    setResultsLoading(true);

    const storage = getStorage();
    const gsReference = ref(
      storage,
      `gs://${process.env.REACT_APP_PRIVATE_BUCKET}/artists/${surveyTypesense.artistGroupId}/setfan/${surveyId}.csv`,
    );

    try {
      const downloadUrl = await getDownloadURL(gsReference);
      if (downloadUrl) {
        const response = await fetch(downloadUrl);
        const blob = await response.blob();
        await logAction("portal_download_results", {
          responses: surveyTypesense?.responses,
          survey: surveyId,
        });
        downloadFromBlob(
          blob,
          `${artistName} - ${
            surveyTypesense.title
          } - ${DateTime.now().toLocaleString()}.csv`,
        );
      }
    } catch (error) {
      console.error(error);
    }

    setResultsLoading(false);
  };

  const getBarsChartData = (
    type: "info-bar" | "horizontal-bar",
    question: Question,
    total: number,
  ): ChartCardProps["chart"] => ({
    type,
    data: Object.entries(question.options)
      .filter(([_, option]) =>
        question.type === "ImageQuestion" ? !!option.src : !!option.label,
      )
      .filter(([_, option]) =>
        question.type === "SongsQuestion" ? !!option.count : true,
      )
      .map(([id, option]) => ({
        label: option.label ?? "",
        value: option.count,
        ...(question.type === "ImageQuestion"
          ? { image: { src: question.options[id].src, alt: option.label } }
          : null),
      }))
      .sort((a, b) => b.value - a.value),
    total,
  });

  const getRatingChartData = (
    question: RankedQuestion,
    total: number,
  ): ChartCardProps["chart"] => {
    return {
      type: "rating",
      data: Object.entries(question.options).map(
        ([score, { label, count }]) => ({
          score: +score,
          label: label ?? score,
          count,
        }),
      ),
      total,
    };
  };

  const getWordCloudData = (
    question: TextQuestion,
  ): ChartCardProps["chart"] => {
    let data = Object.entries(question.options ?? {})
      .map(([label, count]) => ({
        text: label,
        value: count,
      }))
      .sort((a, b) => b.value - a.value);

    data = data.slice(
      0,
      Math.min(data.length, isMobile ? 20 : isTablet ? 30 : 40),
    );
    return {
      type: "word-cloud",
      data,
    };
  };

  const ageChart: ChartCardProps["chart"] = {
    type: "donut",
    data: aggregations
      ? Object.entries(aggregations.age?.options ?? {})
          .sort(([optionIdA], [optionIdB]) =>
            optionIdA.localeCompare(optionIdB),
          )
          .map(([optionId, option]) => ({
            id: optionId,
            label: `${option.label} years old`,
            value: option.count,
          }))
      : [],
  };

  const genderChart: ChartCardProps["chart"] = {
    type: "donut",
    data: aggregations
      ? Object.entries(aggregations.gender?.options ?? {})
          .sort(([optionIdA], [optionIdB]) =>
            optionIdA.localeCompare(optionIdB),
          )
          .map(([optionId, option]) => ({
            id: optionId,
            label: option.label,
            value: option.count,
          }))
      : [],
  };

  const surveyQuestionsContextValue: Context = useMemo(
    () => ({ questions: allRespondentsQuestions }),
    [allRespondentsQuestions],
  );

  const chartCards = useMemo(
    () =>
      !aggregationsLoading &&
      !surveyDocLoading &&
      aggregations &&
      surveyQuestions &&
      surveyQuestions.map((question) => {
        const filterQuestion = aggregations[selectedQuestion];
        const totalResponses =
          selectedOption !== "all" && filterQuestion?.type !== "TextQuestion"
            ? filterQuestion?.options?.[selectedOption]?.count
            : surveyTypesense?.responses;

        const dataMap = [
          {
            types: [
              "Autocomplete",
              "Dropdown",
              "SingleSelectQuestion",
              "MultipleSelectQuestion",
              "SongsQuestion",
            ],
            getData: () =>
              getBarsChartData("horizontal-bar", question, totalResponses),
          },
          {
            types: ["ImageQuestion"],
            getData: () =>
              getBarsChartData("info-bar", question, totalResponses),
          },
          {
            types: ["RankedQuestion"],
            getData: () =>
              question.type === "RankedQuestion" &&
              getRatingChartData(question, totalResponses),
          },
          {
            types: ["TextQuestion"],
            getData: () =>
              question.type === "TextQuestion" && getWordCloudData(question),
          },
        ];

        const getData = dataMap.find((elem) =>
          elem.types.includes(question.type),
        )?.getData;

        const chart = getData && getData();
        if (chart) {
          const chartCard = (
            <ChartCard
              key={question.header}
              title={question.header}
              chart={chart}
              {...(question.type === "TextQuestion" && {
                modal: {
                  buttonText: "See Details",
                  component: FreeResponsesModal,
                  props: {
                    questionId: question.id,
                    title: question.header,
                  },
                },
              })}
            />
          );

          if (question.type === "TextQuestion") {
            return (
              <SurveyQuestionsContext.Provider
                key={`${question.header}-provider`}
                value={surveyQuestionsContextValue}
              >
                {chartCard}
              </SurveyQuestionsContext.Provider>
            );
          } else {
            return chartCard;
          }
        } else {
          return null;
        }
      }),
    [
      aggregationsLoading,
      surveyDocLoading,
      aggregations,
      surveyQuestions,
      selectedQuestion,
      selectedOption,
      surveyTypesense,
      // read through `getWordCloudData`, which caps items per breakpoint
      isMobile,
      isTablet,
    ],
  );

  // `useDocumentData` yields `undefined` once loading finishes if the document
  // does not exist. That is the genuinely-empty case and is distinct from both
  // "still loading" and "loaded, but `surveyQuestions` not derived yet".
  // A failed or forbidden read leaves `aggregations` undefined too, so it is
  // excluded here rather than being reported to the artist as empty data.
  const aggregationsMissing =
    !aggregationsLoading &&
    !surveyDocLoading &&
    !aggregationsError &&
    !aggregations;

  // The context reported alongside a failed read. It is kept in a ref and read
  // at capture time rather than depended on below: `useDocumentData` clears a
  // previous error from an effect, so the render that applies a new filter
  // still carries the old error object. Depending on the filter values would
  // therefore re-capture — and re-toast — that same failure on every filter
  // change. This effect is declared first, so it has already committed by the
  // time the reporter below runs and the tags still describe the read that
  // actually failed.
  const errorContextRef = useRef({
    surveyId,
    selectedQuestion,
    selectedOption,
  });
  useEffect(() => {
    errorContextRef.current = { surveyId, selectedQuestion, selectedOption };
  }, [surveyId, selectedQuestion, selectedOption]);

  // One listener failure, one report: only a genuinely new error object from
  // `useDocumentData` re-runs this.
  useEffect(() => {
    if (aggregationsError) {
      Sentry.captureException(aggregationsError, {
        tags: { ...errorContextRef.current },
      });
      toast("Something went wrong, please try again.");
    }
  }, [aggregationsError]);

  const DemographicsHeading = isMobile ? H3 : H2;
  useEffect(() => {
    if (!aggregationsLoading && !surveyDocLoading) {
      const getQuestions = (
        filter: (entry: Entries<Questions>[0]) => boolean,
      ) =>
        aggregations
          ? (Object.entries(aggregations) as Entries<Questions>)
              .filter(filter)
              .sort(([aId, aData], [bId, bData]) =>
                surveyTypesense.displayOrder &&
                surveyTypesense.displayOrder[aId] &&
                surveyTypesense.displayOrder[bId]
                  ? surveyTypesense.displayOrder[aId].display -
                    surveyTypesense.displayOrder[bId].display
                  : aData.header.localeCompare(bData.header),
              )
              .map(([id, data]) => ({ ...data, id }))
          : [];

      const array = [
        ...getQuestions(
          ([questionId, question]) =>
            (questionId.includes("-") ||
              // temporary hack to handle static ids in survey templates because they won't have '-'
              questionId.startsWith("question_") ||
              question.type === "SongsQuestion") &&
            !questionId.includes("#"),
        ),
        ...getQuestions(([questionId]) =>
          ["age", "gender", "setliveEvent"].includes(questionId),
        ),
      ];
      setSurveyQuestions(array);

      // The filter's question list is derived once, from the unfiltered
      // aggregation, and then left alone so a selection keeps its options.
      // Seed it from the first non-empty derivation rather than the first one
      // of any kind: a missing or not-yet-written aggregations document
      // derives `[]`, and an empty array is truthy, so a plain "already set?"
      // check would latch that empty list in permanently and leave the filter
      // unpopulated even once the document arrives. Returning `current`
      // unchanged lets React bail out of the re-render.
      setAllRespondentsQuestions((current) =>
        current?.length ? current : array,
      );
    }
    // `aggregations` is a dependency so a document that appears after the
    // first empty snapshot — created, or newly readable — repopulates the
    // page through the live listener instead of requiring a refresh.
  }, [aggregations, aggregationsLoading, surveyDocLoading]);

  return (
    <>
      <BackLink to={`/${artistId}/reporting/set-fan`}>
        <BackIcon /> Surveys
      </BackLink>
      <Stats survey={surveyTypesense} />
      {!!surveyRootDoc?.sweeps &&
        // hide sweepstakes if the artist is an `additional` artist
        !surveyTypesense?.additionalGroups?.includes(artistId) && (
          <SweepsCard survey={surveyTypesense} />
        )}
      <Actions>
        <Filter
          key="survey"
          surveyQuestions={allRespondentsQuestions}
          selectedQuestion={selectedQuestion}
          setSelectedQuestion={setSelectedQuestion}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
        />
        <ExportButton onClick={downloadSummary} disabled={resultsLoading}>
          <DownloadIcon />
          {resultsLoading ? "Loading..." : "Export Results"}
        </ExportButton>
      </Actions>
      {!chartCards ? (
        aggregationsError ? (
          <NoData label="Something went wrong, please try again." />
        ) : aggregationsMissing ? (
          <NoData />
        ) : (
          <Spinner />
        )
      ) : (
        chartCards
      )}
      <StyledDivider />
      {preSaveModule?.data?.releaseId && (
        <PreSaves
          source="setfan"
          releasesIds={[preSaveModule.data.releaseId]}
        />
      )}
      {/* On an errored read `aggregations` is undefined, so these would render
          as confidently-empty charts with `loading={false}`. The error is
          already reported above; suppress them rather than claim no data. */}
      {!aggregationsError && (
        <DemographicsContainer>
          <DemographicsHeading>Demographics</DemographicsHeading>
          <DemographicsChartsContainer>
            <ChartCard
              title="Age"
              chart={ageChart}
              loading={aggregationsLoading}
            />
            <ChartCard
              title="Gender"
              chart={genderChart}
              loading={aggregationsLoading}
            />
          </DemographicsChartsContainer>
          {selectedOption === "all" && (
            <React.Suspense fallback={<Spinner />}>
              {aggregations && (
                <FanLocations
                  dmaData={locations.dma ?? []}
                  worldData={locations.world ?? []}
                />
              )}
            </React.Suspense>
          )}
        </DemographicsContainer>
      )}
    </>
  );
};

export const useSurveyQuestionsContext = (): Context => {
  const context = useContext(SurveyQuestionsContext);

  if (context === undefined) {
    throw new Error(
      "useSurveyQuestionsContext must be used within a Survey Questions Provider",
    );
  }

  return context!;
};

const ExportButton = styled(Button)`
  display: flex;
  justify-content: space-evenly;
  width: 200px;
  background: transparent !important;
  border: 1px solid var(--main-color);
  color: var(--main-color);
  gap: 5px;

  ${({ theme }) => theme.media.mobile} {
    width: 100%;
    justify-content: center;
    margin-bottom: 20px;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin: 30px 0;

  ${({ theme }) => theme.media.mobile} {
    flex-direction: column-reverse;
    align-items: flex-start;
  }

  ${H3} {
    margin-bottom: 5px;
  }
`;

const StyledDivider = styled(Divider)`
  margin-top: 40px;

  ${({ theme }) => theme.media.mobile} {
    margin-top: 30px;
  }
`;

const DemographicsChartsContainer = styled.div`
  display: flex;
  gap: 2%;

  ${({ theme }) => theme.media.mobile} {
    flex-direction: column;
    gap: 0px;
  }
`;

const DemographicsContainer = styled.div`
  margin: 40px 0 20px;

  ${({ theme }) => theme.media.mobile} {
    margin: 20px 0;
  }
`;

const BackLink = styled(Link)`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  text-decoration: none;
  color: var(--main-color);
  font-weight: 500;
`;
